import React, { useRef, useState, useCallback, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { ChartOptions } from "chart.js";
import { format } from "date-fns";
import { NoDataMessage } from "styles/devices/deviceCharts.styles";
import { CHART_COLORS, useChartUi } from "./chartColors";
import { clampChartXToPresent, refreshNoFutureLimit, zoomLimitsNoFuture } from "./clampChartXToPresent";
import { theme } from "styles/theme";

interface CombinedLineChartProps {
  labels: string[];
  fullDates?: string[];
  timestamps?: number[];
  datasets: {
    name: string;
    data: (number | null)[];
    color: string;
    unit?: string;
  }[];
  fromTimestamp?: number;
  toTimestamp?: number;
  zoomKey?: number;
  onResetZoom?: () => void;
  onViewportChange?: (minTimestamp: number) => void;
}

export const CombinedLineChart: React.FC<CombinedLineChartProps> = ({
  labels,
  fullDates,
  timestamps,
  datasets,
  fromTimestamp,
  toTimestamp,
  zoomKey,
  onResetZoom,
  onViewportChange,
}) => {
  const chartRef = useRef<any>(null);
  const chartUi = useChartUi();
  const [isZoomed, setIsZoomed] = useState(false);
  const isZoomedRef = useRef(isZoomed);
  isZoomedRef.current = isZoomed;
  // resetZoom() can fire zoom callbacks — ignore those so NOW stays disabled
  const suppressViewportRef = useRef(false);
  const isFirstZoomKeyRef = useRef(true);

  // Stable refs — declared early so all effects/memos can use them safely.
  const datasetsRef = useRef(datasets);
  datasetsRef.current = datasets;
  const onViewportChangeRef = useRef(onViewportChange);
  onViewportChangeRef.current = onViewportChange;
  const fromTimestampRef = useRef(fromTimestamp);
  fromTimestampRef.current = fromTimestamp;
  const toTimestampRef = useRef(toTimestamp);
  toTimestampRef.current = toTimestamp;

  const runProgrammaticZoomReset = useCallback(() => {
    if (!chartRef.current) return;
    suppressViewportRef.current = true;
    const chart = chartRef.current;
    chart.options.scales!.x!.min = fromTimestampRef.current;
    chart.options.scales!.x!.max = toTimestampRef.current;
    chart.resetZoom();
    setIsZoomed(false);
    requestAnimationFrame(() => {
      suppressViewportRef.current = false;
    });
  }, []);

  // Reset zoom when zoomKey changes (skip mount — avoids false NOW enable)
  React.useEffect(() => {
    if (zoomKey === undefined) return;
    if (isFirstZoomKeyRef.current) {
      isFirstZoomKeyRef.current = false;
      return;
    }
    runProgrammaticZoomReset();
  }, [zoomKey, runProgrammaticZoomReset]);

  const handleResetZoom = useCallback(() => {
    runProgrammaticZoomReset();
    onResetZoom?.();
  }, [onResetZoom, runProgrammaticZoomReset]);
  // Imperatively update scale min/max when fromTimestamp/toTimestamp change but only if not zoomed
  React.useEffect(() => {
    if (chartRef.current && !isZoomed) {
      const chart = chartRef.current;
      chart.options.scales!.x!.min = fromTimestamp;
      chart.options.scales!.x!.max = toTimestamp;
      chart.update("none");
    }
  }, [fromTimestamp, toTimestamp, isZoomed]);

  // Check if there's valid data in any dataset
  const hasValidData = datasets.some((dataset) =>
    dataset.data.some((value) => value !== null && value !== undefined),
  );

  const isLargeDataset = timestamps ? timestamps.length > 200 : false;

  // Map dataset name to appropriate Y-axis
  const getYAxisId = (name: string): string => {
    switch (name) {
      case "pH":
        return "yPh";
      case "ORP":
        return "yOrp";
      case "Flow Status":
        return "yFlow";
      case "Salinity":
        return "ySalinity";
      case "Vsdw":
        return "yVsdw";
      default:
        return "yTemp";
    }
  };

  // Convert flat data arrays to {x, y} format using timestamps
  const chartData = useMemo(
    () => ({
      datasets: datasets.map((dataset) => {
        const isFlowData = dataset.name === "Flow Status";

        // Create {x: timestamp, y: value} data points
        const xyData = timestamps
          ? dataset.data.map((value, i) => ({
              x: timestamps[i],
              y: value,
            }))
          : dataset.data.map((value, i) => ({
              x:
                fromTimestamp! +
                (i * (toTimestamp! - fromTimestamp!)) /
                  Math.max(dataset.data.length - 1, 1),
              y: value,
            }));

        return {
          label: dataset.name,
          data: xyData,
          borderColor: dataset.color,
          backgroundColor: `${dataset.color}33`,
          yAxisID: getYAxisId(dataset.name),
          tension: 0.1,
          pointRadius: isLargeDataset ? 0 : 3,
          pointHitRadius: 5,
          borderWidth: 2,
          fill: false,
          spanGaps: false,
          borderDash: isFlowData ? [5, 5] : undefined,
          // Skip Chart.js internal data parsing — data is already in {x,y} format
          parsing: false,
          // Data is sorted by timestamp — skip sort check
          normalized: true,
        } as any;
      }),
    }),
    [datasets, timestamps, fromTimestamp, toTimestamp, isLargeDataset],
  );

  // Chart options with time scale
  const options: ChartOptions<"line"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      // Disable animations so zoom/pan is instant — avoids per-frame redraw lag
      animation: false,
      interaction: {
        mode: "index" as const,
        intersect: false,
      },
      plugins: {
        decimation: {
          enabled: true,
          algorithm: "lttb" as const,
          samples: 500,
        },
        zoom: {
          limits: zoomLimitsNoFuture(),
          pan: {
            enabled: true,
            mode: "x" as const,
            onPanStart: ({ chart }: any) => {
              refreshNoFutureLimit(chart);
              return true;
            },
            onPan: ({ chart }: any) => {
              refreshNoFutureLimit(chart);
            },
            onPanComplete: ({ chart }: any) => {
              if (suppressViewportRef.current) return;
              clampChartXToPresent(chart);
              if (!isZoomedRef.current) setIsZoomed(true);
              // Only after the user releases pan — avoids a request storm mid-drag
              onViewportChangeRef.current?.(chart.scales.x.min);
            },
          },
          zoom: {
            wheel: {
              enabled: true,
              speed: 0.1,
            },
            pinch: {
              enabled: true,
            },
            mode: "x" as const,
            onZoomStart: ({ chart }: any) => {
              refreshNoFutureLimit(chart);
              return true;
            },
            onZoom: ({ chart }: any) => {
              refreshNoFutureLimit(chart);
            },
            onZoomComplete: ({ chart }: any) => {
              if (suppressViewportRef.current) return;
              clampChartXToPresent(chart);
              if (!isZoomedRef.current) setIsZoomed(true);
              onViewportChangeRef.current?.(chart.scales.x.min);
            },
          },
        },
        legend: {
          position: "top" as const,
          labels: {
            color: chartUi.axis,
          },
        },
        tooltip: {
          enabled: true,
          mode: "index" as const,
          intersect: false,
          backgroundColor: chartUi.tooltipBg,
          titleColor: chartUi.tooltipTitle,
          bodyColor: chartUi.tooltipBody,
          borderColor: chartUi.tooltipBorder,
          borderWidth: 1,
          callbacks: {
            title: function (tooltipItems) {
              const dataPoint = tooltipItems[0].raw as {
                x: number;
                y: number | null;
              };
              if (dataPoint?.x) {
                return format(new Date(dataPoint.x), "MMM dd, yyyy HH:mm");
              }
              return tooltipItems[0].label;
            },
            label: function (context) {
              const dataset = datasetsRef.current[context.datasetIndex];
              const unit = dataset?.unit || "";
              let label = dataset?.name || "";
              if (label) label += ": ";
              if (context.parsed.y === null) return label;
              if (dataset?.name === "Flow Status") {
                label += context.parsed.y > 0 ? "on" : "off";
                return label;
              }
              label += context.parsed.y.toFixed(2);
              if (unit) label += ` ${unit}`;
              return label;
            },
          },
        },
        title: {
          display: true,
          text: "Combined Sensors",
          font: { size: 16 },
          color: chartUi.title,
        },
      },
      scales: {
        x: {
          type: "time" as const,
          time: {
            displayFormats: {
              hour: "HH:mm",
              day: "MMM dd",
              week: "MMM dd",
              month: "MMM yyyy",
            },
            tooltipFormat: "MMM dd, yyyy HH:mm",
          },
          min: fromTimestampRef.current,
          max: toTimestampRef.current,
          grid: {
            color: chartUi.grid,
          },
          ticks: {
            maxTicksLimit: 12,
            maxRotation: 45,
            minRotation: 0,
            color: chartUi.axis,
          },
        },
        yPh: {
          type: "linear" as const,
          position: "left" as const,
          grid: {
            color: chartUi.grid,
          },
          title: {
            display: true,
            text: "pH",
            color: CHART_COLORS.pH,
          },
          ticks: {
            color: CHART_COLORS.pH,
          },
          min: 6.5,
          max: 8.5,
        },
        yOrp: {
          type: "linear" as const,
          position: "left" as const,
          grid: {
            drawOnChartArea: false,
          },
          title: {
            display: true,
            text: "ORP (mV)",
            color: CHART_COLORS.ORP,
          },
          ticks: {
            color: CHART_COLORS.ORP,
          },
          min: 0,
          max: 1000,
        },
        yTemp: {
          type: "linear" as const,
          position: "right" as const,
          grid: {
            drawOnChartArea: false,
          },
          title: {
            display: true,
            text: "Temperature",
            color: CHART_COLORS.Temperature,
          },
          ticks: {
            color: CHART_COLORS.Temperature,
          },
          min: 0,
          max: 60,
        },
        ySalinity: {
          type: "linear" as const,
          position: "right" as const,
          grid: {
            drawOnChartArea: false,
          },
          title: {
            display: true,
            text: "Salinity (ppm)",
            color: CHART_COLORS.Salinity,
          },
          ticks: {
            color: CHART_COLORS.Salinity,
          },
          min: 0,
          max: 10000,
        },
        yVsdw: {
          type: "linear" as const,
          position: "right" as const,
          grid: {
            drawOnChartArea: false,
          },
          title: {
            display: true,
            text: "Vsdw (W)",
            color: CHART_COLORS.Vsdw,
          },
          ticks: {
            color: CHART_COLORS.Vsdw,
          },
          min: 0,
          max: 50,
        },
        yFlow: {
          type: "linear" as const,
          position: "right" as const,
          grid: {
            drawOnChartArea: false,
          },
          title: {
            display: true,
            text: "Flow",
            color: CHART_COLORS.Flow,
          },
          ticks: {
            color: CHART_COLORS.Flow,
            callback: function (value) {
              return value === 0 ? "Off" : "On";
            },
          },
          min: 0,
          max: 1.5,
        },
      },
    }),
    [chartUi],
  );

  // If no valid data and no timestamps, show message (after hooks)
  if (!hasValidData && !timestamps?.length && !fromTimestamp) {
    return (
      <NoDataMessage>
        No data available for the selected time range
      </NoDataMessage>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {isZoomed && (
        <button
          onClick={handleResetZoom}
          style={{
            position: "absolute",
            top: "0.5rem",
            right: "0.5rem",
            background: theme.colors.primaryTransparent,
            border: `1px solid ${theme.colors.tertiary}`,
            color: theme.colors.tertiary,
            padding: "0.25rem 0.5rem",
            borderRadius: theme.borderRadius.small,
            fontSize: "0.7rem",
            cursor: "pointer",
            zIndex: 5,
          }}
        >
          Reset Zoom
        </button>
      )}
      <Line ref={chartRef} options={options} data={chartData} />
    </div>
  );
};
